/** @format */

import { type ChangeEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FileDropZone from '../FileDropZone';
import { DeleteIcon, EditIcon, ThreedotsIcon, VideoEditorIcon } from 'src/components/icons';
import type { ContentEditorPart, ContentPartUploadFn } from 'src/types';

interface ContentEditorBlockBodyVideoProps {
  part: ContentEditorPart;
  onFile?: ContentPartUploadFn;
}

export default function ContentEditorBlockBodyVideo({ part, onFile = undefined }: ContentEditorBlockBodyVideoProps) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const videoPart = part.type === 'video' ? part : null;
  const v = videoPart?.video && typeof videoPart.video === 'object' ? videoPart.video : null;
  const url = v && typeof v.url === 'string' ? v.url : '';
  const showVideo = Boolean(url);
  const uploadFile = Array.isArray(videoPart?._file) ? videoPart._file[0] : videoPart?._file;
  const displayName = uploadFile?.name || (v?.title as string | undefined) || '';
  const inputId = `content-editor-file-${part.id}`;

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const handleChangeVideo = () => {
    setMenuOpen(false);
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleDeleteVideo = () => {
    setMenuOpen(false);
    if (typeof onFile === 'function') {
      onFile(null);
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0];
    if (file && typeof onFile === 'function') {
      onFile(file);
    }
    if (event?.target) {
      event.target.value = '';
    }
  };

  return (
    <div className="content-editor__video-block">
      {showVideo ? (
        <div className="content-editor__video-preview-wrap">
          <div className="content-editor__remote-video-preview">
            <video className="content-editor__remote-video-preview-el" src={url} controls playsInline />
          </div>
          <div className="content-editor__video-actions" ref={menuRef}>
            <button
              type="button"
              className="content-editor__video-actions-toggle"
              onClick={() => setMenuOpen(open => !open)}
              aria-label={t('contentEditor.videoActions')}
              aria-expanded={menuOpen}>
              <ThreedotsIcon />
            </button>
            {menuOpen ? (
              <div className="content-editor__video-actions-menu" role="menu">
                <button
                  type="button"
                  className="content-editor__video-actions-item"
                  role="menuitem"
                  onClick={handleChangeVideo}>
                  <EditIcon width={20} height={20} />
                  <span>{t('common.button.change')}</span>
                </button>
                <button
                  type="button"
                  className="content-editor__video-actions-item"
                  role="menuitem"
                  onClick={handleDeleteVideo}>
                  <DeleteIcon width={20} height={20} />
                  <span>{t('common.button.delete')}</span>
                </button>
              </div>
            ) : null}
            <input
              id={inputId}
              ref={inputRef}
              type="file"
              accept="video/*"
              className="content-editor__dropzone-input"
              onChange={handleFileChange}
            />
          </div>
        </div>
      ) : null}
      {!showVideo ? (
        <FileDropZone
          inputId={inputId}
          accept="video/*"
          label={
            <div className="content-editor__dropzone-label-icon-container">
              <VideoEditorIcon />
              {t('contentEditor.dropVideoLabel')}
            </div>
          }
          fileName={displayName}
          onFile={onFile}
        />
      ) : null}
    </div>
  );
}
